/**
 * Вертикальный Reel (барабан) как в казино-слотах.
 * Красивая анимация: элементы летят сверху вниз, плавно останавливаются.
 */

import * as PIXI from 'pixi.js';
import {
  DRUM_SEGMENTS,
  STATIC_RING_RADII,
  RING_WIDTH,
  type DrumSegmentConfig,
} from '@/config/drumConfig';
import { DRUM_SYMBOL_ASSET, DRUM_SYMBOL_CUT_JUICE, FRUIT_ASSETS } from '@/config/fruitAssets';
import type { DrumSegmentType } from '@/config/fruitAssets';

/** Радиус барабана = внутренний край синего кольца (без чёрного отступа до окружности) */
const DRUM_VIEW_RADIUS = STATIC_RING_RADII.blue - RING_WIDTH;
const REEL_SIZE = DRUM_VIEW_RADIUS * 2;

/** Высота элемента меньше вьюпорта: в центре виден выпавший элемент + части сверху и снизу */
const ELEMENT_HEIGHT = Math.round(REEL_SIZE * 0.55);
const REEL_WIDTH = REEL_SIZE;
/** Отступ между элементами */
const CELL_GAP = 16;
const CELL_STRIDE = ELEMENT_HEIGHT + CELL_GAP;
/** Смещаем ленту на 1 копию вверх, чтобы сверху всегда был буферный элемент */
const REEL_OFFSET = DRUM_SEGMENTS.length * CELL_STRIDE;

/** Номер копии для отображения: центр буфера, чтобы сверху и снизу были видны соседние элементы */
const DISPLAY_COPY_INDEX = 6;

type SpinProfile = {
  baseSpeed: number; // px/sec
  accelMs: number;
  stopMs: number;
  quickStopMs: number;
};

const SPIN_PROFILES: Record<'normal' | 'turbo', SpinProfile> = {
  normal: {
    baseSpeed: 1100,
    accelMs: 160,
    stopMs: 300,
    quickStopMs: 180,
  },
  turbo: {
    baseSpeed: 2200,
    accelMs: 90,
    stopMs: 180,
    quickStopMs: 110,
  },
};

const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);
const easeOutBack = (t: number): number => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};
const easeOutQuint = (t: number): number => 1 - Math.pow(1 - t, 5);

/** Длительности анимации «разрез» при выпадении фрукта (мс) */
const SWORD_WINDUP_MS = 120;
const SWORD_SWING_MS = 180;
const SWORD_STRIKE_MS = 200;
const CUT_SLICE_FLY_MS = 180;      // кадр cut "летит" по диагонали
const CUT_SLICE_HOLD_MS = 140;     // короткая пауза после пролёта
const CUT_SLICE_FADEOUT_MS = 170;  // исчезновение кадра cut
const CUT_JUICE_SPLASH_MS = 260;
const CUT_CROSSFADE_MS = 360;
const CUT_JUICE_FADEOUT_MS = 320;
const CUT_SLICE_START_MS = SWORD_WINDUP_MS + SWORD_SWING_MS + SWORD_STRIKE_MS * 0.45;
const CUT_SLICE_TOTAL_MS = CUT_SLICE_FLY_MS + CUT_SLICE_HOLD_MS + CUT_SLICE_FADEOUT_MS;
export const CUT_ANIM_TOTAL_MS = Math.max(
  CUT_SLICE_START_MS + CUT_SLICE_TOTAL_MS,
  CUT_SLICE_START_MS + CUT_JUICE_SPLASH_MS + CUT_CROSSFADE_MS + CUT_JUICE_FADEOUT_MS
);

export const CUT_SOUND_DELAY_MS = Math.max(0, CUT_SLICE_START_MS - 320);
export const JUICE_SOUND_DELAY_MS = CUT_SLICE_START_MS + CUT_SLICE_FLY_MS * 0.35 + 620;

/** Ячейка с поддержкой анимации разреза (момент разреза → сок → разрезанный) */
interface ReelCellMeta {
  segmentType: DrumSegmentType;
  symbolSprite: PIXI.Sprite;
  cutSprite?: PIXI.Sprite;
  juiceSprite?: PIXI.Sprite;
  cutSliceSprite?: PIXI.Sprite; // картинка момента рассечения (cut)
  swordSprite?: PIXI.Sprite; // меч для взмаха
}

type ScaledSprite = PIXI.Sprite & { baseScale?: number };

export class VerticalReel extends PIXI.Container {
  private elements: DrumSegmentConfig[];
  private elementContainers: (PIXI.Container & ReelCellMeta)[] = [];
  private reelContainer: PIXI.Container;
  private viewportMask!: PIXI.Graphics;
  private yOffset = 0;
  private targetYOffset = 0;
  private isSpinning = false;
  private isStopping = false;
  private stopStartTime = 0;
  private stopStartY = 0;
  private stopDurationMs = 0;
  private stopDistance = 0;
  private currentSpeed = 0;
  private resetTimeoutId: number | null = null;
  private profile: SpinProfile = SPIN_PROFILES.normal;
  /** Анимация разреза на центральной ячейке после остановки */
  private cutAnimation: { cellIndex: number; startTime: number } | null = null;
  private isSpinStretchActive = false;

  constructor() {
    super();
    // Буфер: 12 копий сегментов, чтобы при любом yOffset в [-wrapSize, -DRUM_VIEW_RADIUS] всегда были элементы сверху/снизу
    this.elements = [
      ...DRUM_SEGMENTS,
      ...DRUM_SEGMENTS,
      ...DRUM_SEGMENTS,
      ...DRUM_SEGMENTS,
      ...DRUM_SEGMENTS,
      ...DRUM_SEGMENTS,
      ...DRUM_SEGMENTS,
      ...DRUM_SEGMENTS,
      ...DRUM_SEGMENTS,
      ...DRUM_SEGMENTS,
      ...DRUM_SEGMENTS,
      ...DRUM_SEGMENTS,
    ];
    this.reelContainer = new PIXI.Container();
    this.buildReel();
    this.buildViewport();
    this.reelContainer.mask = this.viewportMask;
    this.setInitialElement(0);
  }


  /** Установить начальный элемент (используем среднюю копию буфера — сверху/снизу видны соседи) */
  setInitialElement(elementIndex: number): void {
    const baseIndex = elementIndex % DRUM_SEGMENTS.length;
    const wrapSize = DRUM_SEGMENTS.length * CELL_STRIDE;
    const displayIndex = baseIndex + DISPLAY_COPY_INDEX * DRUM_SEGMENTS.length;
    const centerY = displayIndex * CELL_STRIDE - REEL_OFFSET + ELEMENT_HEIGHT / 2;
    let y = -centerY;
    while (y >= 0) y -= wrapSize;
    while (y < -wrapSize) y += wrapSize;
    this.yOffset = y;
    this.targetYOffset = y;
    this.reelContainer.y = this.yOffset;
  }

  private buildReel(): void {
    const centerY = ELEMENT_HEIGHT / 2;
    /** Максимальный размер спрайта по большей стороне — вписываем в ячейку, без наложения */
    const targetSize = Math.min(ELEMENT_HEIGHT, REEL_WIDTH) * 1.1 * 1.5;

    for (let i = 0; i < this.elements.length; i++) {
      const seg = this.elements[i];
      const y = i * CELL_STRIDE - REEL_OFFSET;
      const cell = this.createElement(seg, centerY, targetSize);
      cell.y = y;
      this.reelContainer.addChild(cell);
      this.elementContainers.push(cell);
    }
    this.addChild(this.reelContainer);
  }

  private resetCutState(cell: PIXI.Container & ReelCellMeta): void {
    if (!cell.cutSprite) return;
    cell.symbolSprite.alpha = 1;
    cell.cutSprite.alpha = 0;
    if (cell.juiceSprite) {
      cell.juiceSprite.alpha = 0;
      cell.juiceSprite.visible = false;
    }
    if (cell.cutSliceSprite) {
      cell.cutSliceSprite.alpha = 0;
      cell.cutSliceSprite.visible = false;
    }
    if (cell.swordSprite) {
      cell.swordSprite.alpha = 0;
      cell.swordSprite.visible = false;
    }
    const cutBase = (cell.cutSprite as ScaledSprite).baseScale ?? cell.cutSprite.scale.x;
    cell.cutSprite.scale.set(cutBase);
  }

  // /** Масштаб спрайта по целевой длине большей стороны */
  // private scaleSpriteToSize(sprite: PIXI.Sprite, targetSize: number): void {
  //   const tex = sprite.texture;
  //   const scale = targetSize / Math.max(tex.width, tex.height, 1);
  //   sprite.scale.set(scale);
  //   (sprite as ScaledSprite).baseScale = scale;
  // }

  /** Ячейка барабана: для red/green/blue — 3 слоя (целый, разрезанный, сок) для анимации разреза; для gray/skull — один спрайт. */
  private createElement(seg: DrumSegmentConfig, centerY: number, targetSize: number): PIXI.Container & ReelCellMeta {
    const container = new PIXI.Container() as PIXI.Container & ReelCellMeta;
    const scaleFromSize = (w: number, h: number) => targetSize / Math.max(w, h, 1);

    const cutJuice = DRUM_SYMBOL_CUT_JUICE[seg.type as DrumSegmentType];
    if (cutJuice) {
      container.sortableChildren = true;
      // Порядок: сок (сзади), разрезанный, целый, картинка рассечения и меч (спереди). По умолчанию виден только целый.
      const juiceTex = PIXI.Texture.from(cutJuice.juice);
      const cutTex = PIXI.Texture.from(cutJuice.cut);
      const symbolTex = PIXI.Texture.from(cutJuice.symbol);
      const juiceSprite = new PIXI.Sprite(juiceTex);
      const cutSprite = new PIXI.Sprite(cutTex);
      const symbolSprite = new PIXI.Sprite(symbolTex);
      const cutSliceTex = PIXI.Texture.from(FRUIT_ASSETS.cut_slice);
      const cutSliceSprite = new PIXI.Sprite(cutSliceTex);
      const swordTex = PIXI.Texture.from(FRUIT_ASSETS.sword);
      const swordSprite = new PIXI.Sprite(swordTex);
      for (const s of [juiceSprite, cutSprite, symbolSprite, cutSliceSprite, swordSprite]) {
        if (s === swordSprite) {
          // Рукоять справа — якорь ближе к рукояти для естественного взмаха
          s.anchor.set(0.86, 0.5);
        } else {
          s.anchor.set(0.5);
        }
        s.x = 0;
        s.y = centerY;
        if (s.texture.valid && s.texture.width > 0) {
          const scaleTarget = s === swordSprite ? targetSize * 1.25 : targetSize;
          const scale = scaleTarget / Math.max(s.texture.width, s.texture.height, 1);
          s.scale.set(scale);
          (s as ScaledSprite).baseScale = scale;
        } else {
          const scaleTarget = s === swordSprite ? targetSize * 1.25 : targetSize;
          const scale = scaleTarget / 256;
          s.scale.set(scale);
          (s as ScaledSprite).baseScale = scale;
          s.texture.on('update', () => {
            if (s.texture.width > 0) {
              const sizeTarget = s === swordSprite ? targetSize * 1.25 : targetSize;
              const nextScale = sizeTarget / Math.max(s.texture.width, s.texture.height, 1);
              s.scale.set(nextScale);
              (s as ScaledSprite).baseScale = nextScale;
            }
          });
        }
      }
      juiceSprite.visible = false;
      juiceSprite.alpha = 0;
      cutSprite.alpha = 0;
      cutSliceSprite.visible = false;
      cutSliceSprite.alpha = 0;
      swordSprite.visible = false;
      swordSprite.alpha = 0;
      juiceSprite.blendMode = PIXI.BLEND_MODES.NORMAL;
      cutSliceSprite.blendMode = PIXI.BLEND_MODES.ADD;
      swordSprite.blendMode = PIXI.BLEND_MODES.NORMAL;
      juiceSprite.zIndex = -1;
      cutSprite.zIndex = 1;
      symbolSprite.zIndex = 2;
      cutSliceSprite.zIndex = 3;
      swordSprite.zIndex = 4;
      container.addChild(juiceSprite);
      container.addChild(cutSprite);
      container.addChild(symbolSprite);
      container.addChild(cutSliceSprite);
      container.addChild(swordSprite);
      container.segmentType = seg.type as DrumSegmentType;
      container.symbolSprite = symbolSprite;
      container.cutSprite = cutSprite;
      container.juiceSprite = juiceSprite;
      container.cutSliceSprite = cutSliceSprite;
      container.swordSprite = swordSprite;
    } else {
      // gray / skull — один спрайт, анимация разреза не предусмотрена
      const assetUrl = DRUM_SYMBOL_ASSET[seg.type];
      const texture = PIXI.Texture.from(assetUrl);
      const symbolSprite = new PIXI.Sprite(texture);
      symbolSprite.anchor.set(0.5);
      symbolSprite.x = 0;
      symbolSprite.y = centerY;
      if (texture.valid && texture.width > 0) {
        let scale = scaleFromSize(texture.width, texture.height);
        if (seg.type === 'skull') scale /= 1.25;
        if (seg.type === 'gray') scale /= 1.5;
        symbolSprite.scale.set(scale);
        (symbolSprite as ScaledSprite).baseScale = scale;
      } else {
        let scale = scaleFromSize(256, 256);
        if (seg.type === 'skull') scale /= 1.25;
        if (seg.type === 'gray') scale /= 1.5;
        symbolSprite.scale.set(scale);
        (symbolSprite as ScaledSprite).baseScale = scale;
        texture.on('update', () => {
          if (texture.width > 0) {
            let nextScale = scaleFromSize(texture.width, texture.height);
            if (seg.type === 'skull') nextScale /= 1.25;
            if (seg.type === 'gray') nextScale /= 1.5;
            symbolSprite.scale.set(nextScale);
            (symbolSprite as ScaledSprite).baseScale = nextScale;
          }
        });
      }
      container.addChild(symbolSprite);
      container.segmentType = seg.type as DrumSegmentType;
      container.symbolSprite = symbolSprite;
      container.cutSprite = undefined;
      container.juiceSprite = undefined;
    }
    return container;
  }

  private buildViewport(): void {
    const r = REEL_WIDTH / 2;
    this.viewportMask = new PIXI.Graphics();
    this.viewportMask.beginFill(0xffffff, 1);
    this.viewportMask.drawCircle(0, 0, r);
    this.viewportMask.endFill();
    this.addChild(this.viewportMask);

    // Рамка «окна» барабана казино — одна обводка по вьюпорту, без кругов под символами
    const frame = new PIXI.Graphics();
    frame.lineStyle(4, 0x2a2a2a, 1);
    frame.drawCircle(0, 0, r);
    frame.lineStyle(2, 0xe8d4a0, 0.95);
    frame.drawCircle(0, 0, r - 3);
    this.addChild(frame);
  }

  startSpin(turbo = false): void {
    this.isSpinning = true;
    this.isStopping = false;
    this.cutAnimation = null;
    this.currentSpeed = 0;
    if (this.resetTimeoutId !== null) {
      window.clearTimeout(this.resetTimeoutId);
    }
    const resetDelayMs = 900;
    this.resetTimeoutId = window.setTimeout(() => {
      for (const cell of this.elementContainers) {
        if (!cell.cutSprite) continue;
        this.resetCutState(cell);
      }
      this.resetTimeoutId = null;
    }, resetDelayMs);
    this.profile = turbo ? SPIN_PROFILES.turbo : SPIN_PROFILES.normal;
    this.targetYOffset = 0;
  }

  /** Остановить на конкретном индексе. Целевая копия — средняя (DISPLAY_COPY_INDEX), чтобы сверху/снизу были видны соседи. */
  stopAtElement(elementIndex: number, quickStop = false): DrumSegmentConfig {
    this.stopStartY = this.yOffset;
    const baseIndex = elementIndex % DRUM_SEGMENTS.length;
    const wrapSize = DRUM_SEGMENTS.length * CELL_STRIDE;
    const displayIndex = baseIndex + DISPLAY_COPY_INDEX * DRUM_SEGMENTS.length;
    const centerY = displayIndex * CELL_STRIDE - REEL_OFFSET + ELEMENT_HEIGHT / 2;
    // Целевая позиция: центр на displayIndex; выбираем обёртку так, чтобы докрутка шла вниз (yOffset растёт)
    let target = -centerY + wrapSize * Math.ceil((this.stopStartY + centerY) / wrapSize);
    // Держим yOffset в [-wrapSize, 0) для стабильного буфера сверху/снизу
    while (target >= 0) target -= wrapSize;
    while (target < -wrapSize) target += wrapSize;
    this.targetYOffset = target;
    
    // Немедленно переключаемся в режим остановки
    this.isStopping = true;
    this.isSpinning = false;
    this.stopStartTime = performance.now();
    this.stopDurationMs = quickStop ? this.profile.quickStopMs : this.profile.stopMs;
    let distance = this.targetYOffset - this.stopStartY;
    if (distance < 0) {
      distance += wrapSize;
    }
    this.stopDistance = distance;
    return DRUM_SEGMENTS[baseIndex];
  }

  /** Индекс ячейки в центре вьюпорта (по текущему yOffset; fallback если centerCellIndexWhenStopped не задан) */
  private getCenterCellIndex(): number {
    const centerY = ELEMENT_HEIGHT / 2;
    const raw = (REEL_OFFSET - centerY - this.yOffset) / CELL_STRIDE;
    const idx = Math.round(raw);
    const len = this.elements.length;
    return ((idx % len) + len) % len;
  }

  /** Запустить анимацию разреза на центральной ячейке (только для red/green/blue; для gray/skull не предусмотрено) */
  private tryStartCutAnimation(): boolean {
    const cellIndex = this.getCenterCellIndex();
    const cell = this.elementContainers[cellIndex];
    if (!cell) return false;
    const segType = cell.segmentType;
    if (segType !== 'red' && segType !== 'green' && segType !== 'blue') return false;
    if (!cell.cutSprite || !cell.juiceSprite) return false;
    cell.juiceSprite.visible = true;
    cell.juiceSprite.alpha = 0;
    const juiceBase = (cell.juiceSprite as ScaledSprite).baseScale ?? cell.juiceSprite.scale.x;
    cell.juiceSprite.scale.set(juiceBase * 0.35);
    cell.cutSprite.alpha = 0;
    cell.symbolSprite.alpha = 1;
    if (cell.cutSliceSprite) {
      cell.cutSliceSprite.visible = true;
      cell.cutSliceSprite.alpha = 0;
      const sliceBase = (cell.cutSliceSprite as ScaledSprite).baseScale ?? cell.cutSliceSprite.scale.x;
      cell.cutSliceSprite.scale.set(sliceBase * 0.7);
      cell.cutSliceSprite.rotation = (Math.random() * 0.5 - 0.25);
    }
    if (cell.swordSprite) {
      cell.swordSprite.visible = true;
      cell.swordSprite.alpha = 0;
      const swordBase = (cell.swordSprite as ScaledSprite).baseScale ?? cell.swordSprite.scale.x;
      cell.swordSprite.scale.set(swordBase);
    }
    this.cutAnimation = { cellIndex, startTime: performance.now() };
    return true;
  }

  private updateCutAnimation(now: number): void {
    if (!this.cutAnimation) return;
    const { cellIndex, startTime } = this.cutAnimation;
    const cell = this.elementContainers[cellIndex];
    if (!cell?.cutSprite || !cell?.juiceSprite || (cell.segmentType !== 'red' && cell.segmentType !== 'green' && cell.segmentType !== 'blue')) {
      this.cutAnimation = null;
      return;
    }
    const elapsed = now - startTime;
    const { symbolSprite, cutSprite, juiceSprite } = cell;
    const cutSliceSprite = cell.cutSliceSprite;
    const swordSprite = cell.swordSprite;
    const baseX = symbolSprite.x;
    const baseY = symbolSprite.y;
    const swordSwingRad = 0.9;
    const swordOffsetX = REEL_WIDTH * 0.22;
    const swordOffsetY = REEL_WIDTH * -0.08;

    if (elapsed >= CUT_ANIM_TOTAL_MS) {
      symbolSprite.alpha = 0;
      cutSprite.alpha = 1;
      const cutBase = (cutSprite as ScaledSprite).baseScale ?? cutSprite.scale.x;
      cutSprite.scale.set(cutBase);
      juiceSprite.alpha = 0;
      juiceSprite.visible = false;
      juiceSprite.y = baseY;
      if (cutSliceSprite) {
        cutSliceSprite.alpha = 0;
        cutSliceSprite.visible = false;
        cutSliceSprite.y = baseY;
      }
      if (swordSprite) {
        swordSprite.alpha = 0;
        swordSprite.visible = false;
        swordSprite.y = baseY;
      }
      this.cutAnimation = null;
      return;
    }

    // 1) Взмах меча
    if (swordSprite) {
      const swordBase = (swordSprite as ScaledSprite).baseScale ?? swordSprite.scale.x;
      const startRot = -swordSwingRad * 1.0;
      const windupRot = swordSwingRad * 1.05;
      const strikeRot = -swordSwingRad * 1.95;
      if (elapsed < SWORD_WINDUP_MS) {
        const t = elapsed / SWORD_WINDUP_MS;
        swordSprite.alpha = Math.min(1, t * 1.2);
        swordSprite.rotation = startRot;
        swordSprite.x = baseX + swordOffsetX;
        swordSprite.y = baseY + swordOffsetY;
      } else if (elapsed < SWORD_WINDUP_MS + SWORD_SWING_MS) {
        const t = (elapsed - SWORD_WINDUP_MS) / SWORD_SWING_MS;
        const eased = easeOutCubic(t);
        swordSprite.alpha = 1;
        // Взмах (по часовой) до позиции удара
        swordSprite.rotation = startRot + (windupRot - startRot) * eased;
        swordSprite.x = baseX + swordOffsetX + Math.sin(eased * Math.PI) * REEL_WIDTH * 0.02;
        swordSprite.y = baseY + swordOffsetY + Math.sin(eased * Math.PI) * -REEL_WIDTH * 0.02;
      } else if (elapsed < SWORD_WINDUP_MS + SWORD_SWING_MS + SWORD_STRIKE_MS) {
        const t = (elapsed - (SWORD_WINDUP_MS + SWORD_SWING_MS)) / SWORD_STRIKE_MS;
        const eased = easeOutCubic(t);
        swordSprite.alpha = 1;
        // Удар (против часовой) обратно
        swordSprite.rotation = windupRot + (strikeRot - windupRot) * eased;
        swordSprite.x = baseX + swordOffsetX - Math.sin(eased * Math.PI) * REEL_WIDTH * 0.03;
        swordSprite.y = baseY + swordOffsetY + Math.sin(eased * Math.PI) * REEL_WIDTH * 0.03;
      } else {
        const impactHoldMs = 90;
        const fadeMs = 160;
        const impactElapsed = elapsed - (SWORD_WINDUP_MS + SWORD_SWING_MS + SWORD_STRIKE_MS);
        if (impactElapsed < impactHoldMs) {
          swordSprite.alpha = 1;
          swordSprite.rotation = strikeRot;
        } else {
          const t = Math.min(1, (impactElapsed - impactHoldMs) / fadeMs);
          swordSprite.alpha = 1 - t;
          swordSprite.rotation = strikeRot - t * 0.25;
        }
      }
      swordSprite.scale.set(swordBase);
      swordSprite.visible = swordSprite.alpha > 0.01;
    }

    // 2) Момент рассечения — кадр cut летит после взмаха меча
    if (cutSliceSprite) {
      const sliceBase = (cutSliceSprite as ScaledSprite).baseScale ?? cutSliceSprite.scale.x;
      const sliceElapsed = elapsed - CUT_SLICE_START_MS;
      let sliceAlpha = 0;
      let sliceScale = 0.7;
      const tipPos = new PIXI.Point(baseX, baseY);
      const cutAngle = -0.2 + Math.PI / 6 + 4 * Math.PI / 18;
      if (sliceElapsed >= 0 && sliceElapsed < CUT_SLICE_FLY_MS) {
        const t = sliceElapsed / CUT_SLICE_FLY_MS;
        const eased = easeOutCubic(t);
        sliceAlpha = Math.min(1, t * 1.8);
        sliceScale = 0.7 + 0.8 * eased;
        const lead = 6 + 10 * eased;
        cutSliceSprite.x = tipPos.x + Math.cos(cutAngle) * lead;
        cutSliceSprite.y = tipPos.y + Math.sin(cutAngle) * lead;
      } else if (sliceElapsed >= CUT_SLICE_FLY_MS && sliceElapsed < CUT_SLICE_FLY_MS + CUT_SLICE_HOLD_MS) {
        sliceAlpha = 1;
        sliceScale = 1.35;
        cutSliceSprite.x = tipPos.x;
        cutSliceSprite.y = tipPos.y;
      } else if (sliceElapsed >= CUT_SLICE_FLY_MS + CUT_SLICE_HOLD_MS && sliceElapsed < CUT_SLICE_TOTAL_MS) {
        const fadeElapsed = sliceElapsed - CUT_SLICE_FLY_MS - CUT_SLICE_HOLD_MS;
        sliceAlpha = Math.max(0, 1 - fadeElapsed / CUT_SLICE_FADEOUT_MS);
        sliceScale = 1.35;
        cutSliceSprite.x = tipPos.x;
        cutSliceSprite.y = tipPos.y;
      } else {
        sliceAlpha = 0;
        cutSliceSprite.x = baseX;
        cutSliceSprite.y = baseY;
      }
      cutSliceSprite.alpha = sliceAlpha;
      cutSliceSprite.scale.set(sliceBase * sliceScale);
      cutSliceSprite.rotation = cutAngle;
      cutSliceSprite.visible = sliceAlpha > 0;
    }

    const juiceStart = CUT_SLICE_START_MS + CUT_SLICE_FLY_MS * 0.35;
    const crossfadeStart = CUT_SLICE_START_MS + CUT_SLICE_FLY_MS * 0.5;
    const fadeOutStart = crossfadeStart + CUT_CROSSFADE_MS * 0.55;

    // 2) Сок: вылетает после момента разреза (всплеск), затем исчезает
    const splashT = Math.min(1, Math.max(0, elapsed - juiceStart) / CUT_JUICE_SPLASH_MS);
    const splashScale = 0.7 + 0.8 * easeOutBack(splashT);
    const juiceBase = (juiceSprite as ScaledSprite).baseScale ?? juiceSprite.scale.x;
    juiceSprite.scale.set(juiceBase * splashScale * 0.5);
    juiceSprite.y = baseY + 6 + Math.sin(splashT * Math.PI) * 10;
    let juiceAlpha = 0;
    if (elapsed < juiceStart + CUT_JUICE_SPLASH_MS) {
      juiceAlpha = Math.min(1, splashT * 1.7);
    } else if (elapsed >= fadeOutStart) {
      juiceAlpha = Math.max(0, 1 - (elapsed - fadeOutStart) / CUT_JUICE_FADEOUT_MS);
    } else {
      juiceAlpha = 1;
    }
    juiceSprite.alpha = juiceAlpha;
    juiceSprite.visible = juiceAlpha > 0;

    // 3) Кроссфейд целый → разрезанный (параллельно с соком)
    const crossfadeElapsed = elapsed - crossfadeStart;
    const crossfadeT = Math.max(0, Math.min(1, crossfadeElapsed / CUT_CROSSFADE_MS));
    const crossfadeEased = easeOutQuint(crossfadeT);
    symbolSprite.alpha = 1 - crossfadeEased;
    cutSprite.alpha = crossfadeEased;
    if (cutSprite.alpha > 0) {
      const cutBase = (cutSprite as ScaledSprite).baseScale ?? cutSprite.scale.x;
      const pulse = 1 + 0.08 * Math.sin((elapsed / 1000) * Math.PI * 6);
      cutSprite.scale.set(cutBase * pulse);
    }
  }

  update(delta: number): boolean {
    const wrapSize = DRUM_SEGMENTS.length * CELL_STRIDE;
    const dtSeconds = delta / 60;
    const dtMs = dtSeconds * 1000;
    const now = performance.now();

    if (this.cutAnimation) {
      this.updateCutAnimation(now);
    }

    if (this.isStopping) {
      const elapsed = now - this.stopStartTime;
      const progress = Math.min(1, elapsed / this.stopDurationMs);
      const eased = easeOutCubic(progress);
      
      // Если прошло достаточно времени или очень близко к цели
      if (progress >= 1 || Math.abs(this.stopDistance) < 0.5) {
        this.yOffset = this.targetYOffset;
        this.reelContainer.y = this.yOffset;
        this.isStopping = false;
        return false;
      }
      
      // Плавное торможение с ease-out
      const rawY = this.stopStartY + this.stopDistance * eased;
      this.yOffset = rawY;
      
      // Обертка: держим yOffset в [-wrapSize, 0) для стабильного буфера
      while (this.yOffset < -wrapSize) {
        this.yOffset += wrapSize;
      }
      while (this.yOffset >= 0) {
        this.yOffset -= wrapSize;
      }
      
      this.reelContainer.y = this.yOffset;
      this.setSpinStretch(false);
      return true;
    }
    
    if (this.isSpinning) {
      // Продолжаем спин только если не в режиме остановки
      if (!this.isStopping) {
        const targetSpeed = this.profile.baseSpeed;
        const smoothingMs = 180;
        const lerpT = 1 - Math.exp(-dtMs / smoothingMs);
        this.currentSpeed += (targetSpeed - this.currentSpeed) * lerpT;
        this.yOffset += this.currentSpeed * dtSeconds;

        let wrapped = false;
        while (this.yOffset < -wrapSize) {
          this.yOffset += wrapSize;
          wrapped = true;
        }
        // Держим yOffset в [-wrapSize, 0) для стабильного буфера
        while (this.yOffset >= 0) {
          this.yOffset -= wrapSize;
        }

        if (wrapped) {
          for (const cell of this.elementContainers) {
            if (!cell.cutSprite) continue;
            this.resetCutState(cell);
          }
        }
        
        this.reelContainer.y = this.yOffset;
        this.setSpinStretch(true);
        return true;
      }
      // Если isStopping установлен, переходим к логике остановки
      return true;
    }

    this.currentSpeed = 0;
    this.setSpinStretch(false);
    return false;
  }

  private setSpinStretch(active: boolean): void {
    if (this.isSpinStretchActive === active) return;
    this.isSpinStretchActive = active;
    const stretch = active ? 1.12 : 1;
    for (const cell of this.elementContainers) {
      const sprite = cell.symbolSprite;
      const base = (sprite as ScaledSprite).baseScale ?? sprite.scale.x;
      sprite.scale.set(base, base * stretch);
    }
  }

  /** Вызвать из Game при выпадении фрукта (резервный запуск анимации разреза, если не стартовала при остановке) */
  playCutAnimationIfFruit(): boolean {
    if (this.cutAnimation) return false;
    return this.tryStartCutAnimation();
  }

  getRandomElementIndex(): number {
    return Math.floor(Math.random() * DRUM_SEGMENTS.length);
  }

  highlightElement(index: number, color: number): void {
    const baseIndex = index % DRUM_SEGMENTS.length;
    for (let i = 0; i < 12; i++) {
      const cell = this.elementContainers[baseIndex + i * DRUM_SEGMENTS.length];
      if (!cell?.symbolSprite) continue;
      const setTint = (s: PIXI.Sprite) => {
        s.tint = color;
        setTimeout(() => (s.tint = 0xffffff), 500);
      };
      setTint(cell.symbolSprite);
      if (cell.cutSprite) setTint(cell.cutSprite);
      if (cell.juiceSprite) setTint(cell.juiceSprite);
      if (cell.cutSliceSprite) setTint(cell.cutSliceSprite);
    }
  }
}
